/**
 * Supabase Connection Optimizer
 * Implements connection pooling, query optimization, and performance monitoring
 */

import { supabase } from '@/integrations/supabase/client';

interface QueryMetrics {
  tableName: string;
  operation: string;
  duration: number;
  success: boolean;
  timestamp: number;
}

class SupabaseOptimizer {
  private static instance: SupabaseOptimizer;
  private queryMetrics: QueryMetrics[] = [];
  private connectionPool = {
    activeConnections: 0,
    maxConnections: 10,
    connectionTimeout: 5000
  };

  static getInstance(): SupabaseOptimizer {
    if (!SupabaseOptimizer.instance) {
      SupabaseOptimizer.instance = new SupabaseOptimizer();
    }
    return SupabaseOptimizer.instance;
  }

  /**
   * Optimized profile query with performance monitoring
   */
  async optimizedProfileQuery(userId: string, timeoutMs: number = 3000) {
    const startTime = Date.now();
    let success = false;

    try {
      // Use connection pool management
      if (this.connectionPool.activeConnections >= this.connectionPool.maxConnections) {
        throw new Error('Connection pool exhausted');
      }

      this.connectionPool.activeConnections++;

      const { data, error } = await Promise.race([
        supabase
          .from('profiles')
          .select('id, email, first_name, last_name, phone, department, role, account_status, company_id, avatar_url, created_at, updated_at')
          .eq('id', userId)
          .maybeSingle(),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error(`Optimized query timeout after ${timeoutMs}ms`)), timeoutMs)
        )
      ]);

      success = true;
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    } finally {
      const duration = Date.now() - startTime;
      this.connectionPool.activeConnections = Math.max(0, this.connectionPool.activeConnections - 1);
      
      // Log metrics for monitoring
      this.logQueryMetrics({
        tableName: 'profiles',
        operation: 'SELECT',
        duration,
        success,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Bulk profile query optimization for multiple users
   */
  async bulkProfileQuery(userIds: string[], timeoutMs: number = 5000) {
    const startTime = Date.now();
    let success = false;

    try {
      this.connectionPool.activeConnections++;

      const { data, error } = await Promise.race([
        supabase
          .from('profiles')
          .select('id, email, first_name, last_name, phone, department, role, account_status, company_id, avatar_url, created_at, updated_at')
          .in('id', userIds),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error(`Bulk query timeout after ${timeoutMs}ms`)), timeoutMs)
        )
      ]);

      success = true;
      
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    } finally {
      const duration = Date.now() - startTime;
      this.connectionPool.activeConnections = Math.max(0, this.connectionPool.activeConnections - 1);
      
      this.logQueryMetrics({
        tableName: 'profiles',
        operation: 'BULK_SELECT',
        duration,
        success,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Get connection pool status
   */
  getConnectionPoolStatus() {
    return {
      active: this.connectionPool.activeConnections,
      max: this.connectionPool.maxConnections,
      available: this.connectionPool.maxConnections - this.connectionPool.activeConnections,
      utilization: (this.connectionPool.activeConnections / this.connectionPool.maxConnections) * 100
    };
  }

  /**
   * Get performance metrics for the last hour
   */
  getPerformanceMetrics() {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    const recentMetrics = this.queryMetrics.filter(m => m.timestamp > oneHourAgo);
    
    if (recentMetrics.length === 0) {
      return {
        totalQueries: 0,
        averageDuration: 0,
        successRate: 100,
        slowQueries: 0
      };
    }

    const totalQueries = recentMetrics.length;
    const successfulQueries = recentMetrics.filter(m => m.success).length;
    const averageDuration = recentMetrics.reduce((sum, m) => sum + m.duration, 0) / totalQueries;
    const slowQueries = recentMetrics.filter(m => m.duration > 2000).length;

    return {
      totalQueries,
      averageDuration: Math.round(averageDuration),
      successRate: Math.round((successfulQueries / totalQueries) * 100),
      slowQueries
    };
  }

  /**
   * Log query metrics for performance monitoring
   */
  private logQueryMetrics(metrics: QueryMetrics) {
    this.queryMetrics.push(metrics);
    
    // Keep only last 1000 metrics to prevent memory bloat
    if (this.queryMetrics.length > 1000) {
      this.queryMetrics = this.queryMetrics.slice(-1000);
    }

    // Log slow queries for debugging
    if (metrics.duration > 2000) {
      console.warn('[SupabaseOptimizer] Slow query detected:', {
        table: metrics.tableName,
        operation: metrics.operation,
        duration: `${metrics.duration}ms`,
        success: metrics.success
      });
    }
  }

  /**
   * Clear old metrics (called periodically to free memory)
   */
  clearOldMetrics() {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    this.queryMetrics = this.queryMetrics.filter(m => m.timestamp > oneHourAgo);
  }
}

export const supabaseOptimizer = SupabaseOptimizer.getInstance();

// Clear old metrics every 10 minutes
setInterval(() => {
  supabaseOptimizer.clearOldMetrics();
}, 10 * 60 * 1000);
